import { useQuery } from '@apollo/client';
import CheckmarkOrb from '@clearkit/icons/glyphs/CheckmarkOrb';
import Clock from '@clearkit/icons/glyphs/Clock';
import CloseOrb from '@clearkit/icons/glyphs/CloseOrb';
import Companies from '@clearkit/icons/glyphs/Companies';
import Filter from '@clearkit/icons/glyphs/Filter';
import People from '@clearkit/icons/glyphs/People';
import Search from '@clearkit/icons/glyphs/Search';
import Warning from '@clearkit/icons/glyphs/Warning';
import {
  CKBox,
  CKButton,
  CKField,
  CKFieldLeftElement,
  CKTab,
  CKTabList,
  CKTabs,
  CKTextField,
} from '@clearkit/react';
import capitalize from 'lodash/capitalize';
import moment from 'moment';
import { Link } from 'react-router-dom';

import ConnectorLayout from '~/components/layouts/ConnectorLayout';
import loader from '~/images/loader.png';
import Tile from '~/images/Tiles/Enrichment.svg';
import {
  filters,
  filterToStatuses,
  objectTypes,
  readableFilter,
  readableType,
  statuses,
  typeCompany,
} from '~/lib/dataEnrichment';
import history from '~/lib/history';
import paths from '~/lib/paths';
import { connectorLocations } from '~/lib/segmentAnalytics';

import Error from './Error';
import NoSearchResults from './NoSearchResults.svg';
import Placeholder from './Placeholder';
import query from './query.graphql';
import styles from './styles';

function EnrichmentLogsPage({ classes, enrichmentLogsType }) {
  const [searchFilter, _setSearchFilter] = useState(filters.all);
  const [searchQuery, setSearchQuery] = useState(undefined);

  const {
    data: { enrichmentLogs = [] },
    error,
    loading,
    refetch,
  } = useQuery(query, {
    returnPartialData: true,
    variables: {
      query: searchQuery,
      state: filterToStatuses(searchFilter),
    },
  });

  const integrationObjectTypes = objectTypes[enrichmentLogsType];

  const showEnrichmentLogsPlaceholder =
    enrichmentLogs.length === 0 &&
    searchQuery === undefined &&
    !error &&
    !loading;

  return (
    <div className="w-full bg-gray-50">
      <ConnectorLayout
        integration={enrichmentLogsType}
        connectorLocation={connectorLocations.enrichment}
        subtitle={`Sync Clearbit data to ${readableType(enrichmentLogsType)}`}
        breadcrumb={[
          {
            title: 'Data Sync',
            path: paths.dataEnrichmentIndex(),
          },
        ]}
        tabs={
          <CKTabs
            className="h-full"
            index={3}
            onChange={(index) => {
              switch (index) {
                case 0:
                case 1:
                case 2:
                  history.push(
                    paths.dataEnrichmentObject(
                      enrichmentLogsType,
                      integrationObjectTypes[index].name,
                    ),
                  );

                  break;
                case 3:
                  history.push(paths.dataEnrichmentLogs(enrichmentLogsType));

                  break;
              }
            }}
            isBordered>
            <CKTabList aria-label="My tabs">
              {integrationObjectTypes.map((integrationObjectType) => (
                <CKTab
                  key={integrationObjectType.name}
                  icon={
                    integrationObjectType.type === typeCompany ? (
                      <Companies />
                    ) : (
                      <People />
                    )
                  }
                  label={capitalize(integrationObjectType.name)}>
                  {capitalize(integrationObjectType.name)}
                </CKTab>
              ))}

              <CKTab icon={<Search />} label="Enrichment Logs">
                Enrichment Logs
              </CKTab>
            </CKTabList>
          </CKTabs>
        }
        tile={<Tile />}>
        {showEnrichmentLogsPlaceholder ? (
          <Placeholder className="mx-auto my-32" />
        ) : (
          <div className="w-full px-10 py-6 mx-auto">
            <EnrichmentLogsSearchForm
              defaultValue={searchQuery}
              enrichmentLogsType={capitalize(enrichmentLogsType)}
              error={error}
              onChange={setSearchQuery}
            />

            {error ? (
              <Error
                className="mx-auto mt-16"
                loading={loading}
                refetch={refetch}
              />
            ) : (
              <>
                {searchQuery == null || searchQuery === '' ? (
                  <h1 className="flex items-center mt-5 text-base font-medium text-gray-600">
                    <Clock className="ml-4 mr-2 text-current" />
                    Recently enriched {capitalize(enrichmentLogsType)} records
                  </h1>
                ) : (
                  <h1 className="flex items-center mt-5 text-base font-medium text-gray-600">
                    <Filter className="ml-4 mr-2 text-current" />
                    Displaying most recent {readableFilter(searchFilter)}{' '}
                    matching
                    <strong className="flex items-center ml-1 font-semibold">
                      “{searchQuery}”
                    </strong>
                  </h1>
                )}

                {loading ? (
                  <img alt="" className={classes.loader} src={loader} />
                ) : enrichmentLogs.length > 0 ? (
                  <EnrichmentLogsList
                    className="mt-5"
                    classes={classes}
                    enrichmentLogs={enrichmentLogs}
                    enrichmentLogsType={enrichmentLogsType}
                  />
                ) : (
                  <EnrichmentLogsListEmpty
                    className="mt-40"
                    searchQuery={searchQuery}
                  />
                )}
              </>
            )}
          </div>
        )}
      </ConnectorLayout>
    </div>
  );
}

function EnrichmentLogsSearchForm({
  defaultValue,
  enrichmentLogsType,
  error,
  onChange,
}) {
  const [value, setValue] = useState(defaultValue);

  const isDisabled = !!error || !value;

  return (
    <CKBox>
      <form className="flex items-center p-4">
        <label
          className="pr-6 mr-6 text-base font-medium text-gray-900 border-r border-gray-300"
          htmlFor="query">
          Search {enrichmentLogsType} enrichment logs
        </label>

        <CKField className="flex-1 mr-6">
          <CKTextField
            className="w-full"
            id="query"
            onChange={(event) => {
              if (event.target.value === '') {
                onChange(undefined);
              }
              setValue(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();

                onChange(value);
              }
            }}
            placeholder={`Enter a ${enrichmentLogsType} ID, email address or domain`}
            type="search"
            value={value || ''}
          />

          <CKFieldLeftElement>
            <Search />
          </CKFieldLeftElement>
        </CKField>

        <CKButton
          disabled={isDisabled}
          onClick={() => {
            onChange(value);
          }}
          variant={CKButton.Variant.bold}
          variantColor={CKButton.VariantColor.blue}>
          Search
        </CKButton>
      </form>
    </CKBox>
  );
}

const TIMESTAMP_FORMAT = 'MMMM Do YYYY, h:mm:ss a';

function EnrichmentLogsList({
  className,
  classes,
  enrichmentLogs,
  enrichmentLogsType,
}) {
  const integrationFormatted = readableType(enrichmentLogsType);

  return (
    <CKBox className={classnames('overflow-hidden', className)}>
      <ol className="divide-y divide-gray-200">
        {enrichmentLogs.map((enrichmentLog) => {
          const formattedTimestamp = moment(enrichmentLog.timestamp).format(
            TIMESTAMP_FORMAT,
          );
          const showTypeInfo = enrichmentLog.externalId != 'unknown';

          return (
            <li key={enrichmentLog.id}>
              <Link
                className="flex items-center p-4 hover:bg-gray-50"
                to={paths.dataEnrichmentLog(
                  enrichmentLog.type,
                  enrichmentLog.id,
                )}>
                <EnrichmentLogIcon
                  classes={classes}
                  enrichmentLog={enrichmentLog}
                />

                <span className="ml-3 text-base font-medium text-gray-800">
                  {enrichmentLog.identifier}
                </span>

                <span className="mx-3 text-sm text-gray-500">
                  {capitalize(enrichmentLog.status)}

                  {showTypeInfo ? (
                    <span className="text-gray-400">
                      {' / '}
                      {integrationFormatted} ID: {enrichmentLog.externalId}
                    </span>
                  ) : null}
                </span>

                <time
                  dateTime={enrichmentLog.timestamp}
                  className="ml-auto text-sm font-normal text-gray-400">
                  {formattedTimestamp}
                </time>
              </Link>
            </li>
          );
        })}
      </ol>
    </CKBox>
  );
}

function EnrichmentLogsListEmpty({ className, searchQuery }) {
  return (
    <div className={classnames('flex flex-col items-center', className)}>
      <NoSearchResults className="mb-2.5" />

      <p className="mb-2 text-gray-700 text-md">
        There are no records that match{' '}
        <strong className="font-semibold">“{searchQuery}”</strong>
      </p>
    </div>
  );
}

function EnrichmentLogIcon({ classes, enrichmentLog }) {
  switch (enrichmentLog.status) {
    case statuses.info:
      return <Warning className="text-yellow-400" />;
    case statuses.complete:
      return <CheckmarkOrb className="text-green-400" />;
    case statuses.error:
      return <CloseOrb className="text-red-400" />;
    case statuses.pending:
      return <Clock className="text-gray-400" />;
    default:
      return <div className={classes.iconPlaceholder} />;
  }
}

export default withStyles(styles)(EnrichmentLogsPage);
